import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {GoogleMap} from '@Components/google-map';
import {VenueRecentLocation} from '@Components/event-wizard/venue-recent-location';
import {updatePreviewVenue, updatePreviewVenueAddressDirectionsDetails} from '@Components/event-wizard/preview/preview.helper';
import {useDispatch} from 'react-redux';
import {onEventWizardGoogleMapPlaceSelectionTypeUpdate, onEventWizardOfflineVenueChange} from '@Components/event-wizard/event-wizard-slice';
import {
  useEventWizardGoogleMapAPIKey,
  useEventWizardOfflineVenue,
  useEventWizardOfflineVenueAddressDirections,
  useEventWizardUserPastVenues,
} from '@Components/event-wizard/event-wizard.hooks';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import {PlaceSelectionMethod} from '@Components/google-map/google-map.types';
import {GoogleMapsSelector} from '@Components/google-maps-selector';
import type {VenueOfflineProps} from '@Components/event-wizard/venue-offline/venue-offline.types';
import {GoogleMapSelectorPanelContent} from '@Components/google-map-selector-panel-content';
import {MODALS_IDS} from '@Components/modal-container';
import {useDefaultPanelId} from '@Panels/hooks/useDefaultPanelId';
import {isOfflineVenueComplete} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import {openGoogleMapSelectorModal} from '@Modals/google-map-selector-modal/google-map-selector-modal.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import styles from './venue-offline.module.scss';
import {getGoogleMapEmbedSrcLocation} from '@Components/google-map/google-map.helper';

export function VenueOffline({isMobile = false}: VenueOfflineProps): ReactElement {
  const dispatch = useDispatch();
  const venue = useEventWizardOfflineVenue();
  const addressDirections = useEventWizardOfflineVenueAddressDirections();
  const googleMapAPIKey = useEventWizardGoogleMapAPIKey();
  const userPastVenues = useEventWizardUserPastVenues();

  const [placeDetails, setPlaceDetails] = useState<PlaceDetails | undefined>(venue);

  const updateVenue = (newVenue: PlaceDetails | undefined, placeSelectionMethod: PlaceSelectionMethod | undefined = undefined): void => {
    if (!newVenue) {
      updatePreviewVenue({});
      dispatch(onEventWizardOfflineVenueChange(undefined));
      setPlaceDetails(undefined);
      return;
    }
    dispatch(onEventWizardGoogleMapPlaceSelectionTypeUpdate(placeSelectionMethod));
    setPlaceDetails(newVenue);
    dispatch(onEventWizardOfflineVenueChange(newVenue));
    updatePreviewVenue({offlineVenue: newVenue});
    updatePreviewVenueAddressDirectionsDetails(newVenue.addressDirections ?? '');
  };

  const updateMapAdditionalDetails = (val: string): void => {
    let updatedVenue = venue;

    if (updatedVenue) {
      updatedVenue = {
        ...updatedVenue,
        addressDirections: val,
      };
      dispatch(onEventWizardOfflineVenueChange(updatedVenue));
      updatePreviewVenueAddressDirectionsDetails(val);
    }
  };

  const getMapView = (): ReactElement => {
    return (
      <div className={!venue ? '_hidden' : ''}>
        <div className={`${styles.selectedLocationTitleMap} radius-8 _overflow-hidden`}>
          <div className="flex-row-align-center radius-4 justify-content-between spacing-p-3">
            <div className="flex-column-align-start spacing-p-r-4 -truncated-text">
              <Text className="content-primary -truncated-text _full-width" val={venue ? `${venue.name}` : ''} size={TextSize.XSMALL} bold />
              <Text className="content-primary -truncated-text _full-width" val={venue ? `${venue.address}` : ''} size={TextSize.XXSMALL} />
            </div>
            <Icon
              isUsedAsButton
              className={`${styles.mapActionBtn} ${styles.deleteIcon} spacing-m-r-2`}
              icon="icon-delete"
              shape={IconShape.SQUARE}
              type={IconType.GHOST}
              onClick={() => {
                updateVenue(undefined);
              }}
              size={IconSize.SIZE_ICON_16}
            />
            <Icon
              isUsedAsButton
              className={styles.mapActionBtn}
              icon="icon-pencil"
              shape={IconShape.SQUARE}
              type={IconType.GHOST}
              onClick={() => {
                openGoogleMapSelectorModal({
                  googleMapAPIKey: googleMapAPIKey,
                  onLocationChange: updateVenue,
                  selectedVenue: venue,
                });
              }}
              size={IconSize.SIZE_ICON_16}
            />
          </div>
          <div className={styles.mapSummaryContainer}>
            <iframe
              key={getGoogleMapEmbedSrcLocation(placeDetails, googleMapAPIKey)}
              className={`${styles.mapSummaryView} _full-width _no-border`}
              src={getGoogleMapEmbedSrcLocation(placeDetails, googleMapAPIKey)}
              loading="lazy"
            ></iframe>
          </div>
        </div>
        <div className="spacing-m-t-3">
          <Text className="flex-row-align-center" val={window.i18next.t('pmwjs_directions')} size={TextSize.XSMALL} bold />
          <InputField
            className="spacing-m-t-1"
            onInputChange={updateMapAdditionalDetails}
            size={InputFieldSize.XSMALL}
            input={addressDirections}
            placeholder="Apartment 5, Take the elevator, etc."
          />
        </div>
      </div>
    );
  };

  const getFindLocationView = (): ReactElement => {
    return (
      <div className={venue ? '_hidden' : ''}>
        {userPastVenues.length === 0 && isMobile ? (
          <GoogleMapSelectorPanelContent
            googleMapAPIKey={googleMapAPIKey}
            onLocationChange={updateVenue}
            selectorPanelCTAOptions={{
              modalId: MODALS_IDS.EVENT_WIZARD_VENUE_MODAL,
              panelId: useDefaultPanelId(),
              isLocationSelected: false,
              onSelectedVenuePanelClose: () => {},
              showCloseOption: false,
            }}
          />
        ) : (
          <>
            <div className="flex-row-align-center align-items-start spacing-m-b-3">
              <GoogleMap googleMapAPIKey={googleMapAPIKey} onLocationChange={updateVenue} isAutoCompleteOnly shouldSetUserCurrentLocation={false} isViewOnly={false} />
              <GoogleMapsSelector isFixedSmallIconWidth googleMapAPIKey={googleMapAPIKey} customClasses="spacing-m-l-2" onLocationChange={updateVenue} />
            </div>
            {userPastVenues.length ? (
              <div>
                <Text val={window.i18next.t('pmwjs_recent')} size={TextSize.XXSMALL} bold />
                <div className={`${styles.gap8} spacing-m-t-1 flex-column-align-start`}>{getUserPastVenueContent()}</div>
              </div>
            ) : undefined}
          </>
        )}
      </div>
    );
  };

  const getUserPastVenueContent = (): ReactElement[] => {
    const userPastVenueEls: ReactElement[] = [];

    userPastVenues.forEach((userPastVenue) => {
      if (isOfflineVenueComplete(userPastVenue)) {
        userPastVenueEls.push(
          <VenueRecentLocation
            key={userPastVenue.idAddress}
            place={userPastVenue}
            onClickPlace={(placeDetail: PlaceDetails) => {
              updateVenue(placeDetail, PlaceSelectionMethod.PAST_VENUE);
            }}
          />
        );
      }
    });
    return userPastVenueEls;
  };

  return (
    <>
      {getMapView()}
      {getFindLocationView()}
    </>
  );
}
